// LoginModal.tsx
import React, { useMemo, useState } from "react";
import Modal from "react-modal";
import axios, { AxiosError } from "axios";
import { GetBaseURL } from "./utils/urlUtils";
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Shield,
  Clock,
  CheckCircle,
} from "lucide-react";
import Logo from "../assets/KavoaLogo.png";

Modal.setAppElement("#root");

interface LoginModalProps {
  onSuccessfulLogin: (data: { token: string; userId: string }) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  onSuccessfulLogin,
  isOpen,
  onOpenChange,
}) => {
  const isControlled = typeof isOpen === "boolean" && !!onOpenChange;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? isOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) onOpenChange!(next);
    else setInternalOpen(next);
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validateEmail = (val: string) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(val).toLowerCase());
  };

  const handleResetPassword = async (event: React.MouseEvent) => {
    event.preventDefault();

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email.");
      return;
    }

    try {
      const response = await axios.post(
        `${GetBaseURL()}/api/auth/reset-password`,
        {
          email,
        },
      );
      if (response.data) {
        setEmailSent(true);
        setEmailSentTo(email);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && !error.response) {
        setErrorMessage("Could not connect to the server. Please check your internet connection.");
      } else {
        setErrorMessage("Failed to send reset email. Please try again.");
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);

    try {
      const response = await axios.post(`${GetBaseURL()}/api/auth/login`, {
        email,
        password,
      });

      if (response.data.token && response.data.user.id) {
        onSuccessfulLogin({
          token: response.data.token,
          userId: response.data.user.id,
        });
        localStorage.setItem("accessToken", response.data.token);
        setOpen(false);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (!axiosError.response) {
          setErrorMessage("Could not connect to the server. Please check your internet connection and try again.");
        } else {
          setErrorMessage((axiosError.response.data as any)?.msg || "Invalid email or password.");
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const modalClassName = useMemo(
    () =>
      "w-[95vw] max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-100 outline-none overflow-hidden",
    [],
  );
  const overlayClassName = useMemo(
    () =>
      "fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]",
    [],
  );

  return (
    <Modal
      isOpen={open}
      onRequestClose={() => setOpen(false)}
      contentLabel="Log In Modal"
      className={modalClassName}
      overlayClassName={overlayClassName}
    >
      <div className="md:flex relative">
        {/* Close button */}
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Left branded panel */}
        <div
          className="hidden md:flex md:w-[44%] flex-col justify-between p-8 rounded-l-2xl"
          style={{ backgroundColor: "#0F5132" }}
        >
          <div>
            <img
              src={Logo}
              alt="Kavoa"
              className="h-14 mb-10 brightness-0 invert"
            />
            <h2 className="text-2xl font-bold text-white mb-2">
              Welcome Back
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">
              Sign in to manage your practice
            </p>
          </div>

          <div className="space-y-4 mt-10">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/10">
                <Shield className="w-4 h-4 text-white/60" />
              </div>
              <span className="text-sm text-white/70">
                Secure &amp; encrypted
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/10">
                <Clock className="w-4 h-4 text-white/60" />
              </div>
              <span className="text-sm text-white/70">99.9% uptime</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/10">
                <Lock className="w-4 h-4 text-white/60" />
              </div>
              <span className="text-sm text-white/70">
                Your data, your control
              </span>
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex-1 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-800">
              Sign in to Kavoa
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Enter your credentials to access your account
            </p>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="mb-5 flex items-start gap-3 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl p-3.5">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Reset password success banner */}
          {emailSent && (
            <div className="mb-5 flex items-start gap-3 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5">
              <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                Password reset email sent to{" "}
                <span className="font-semibold">{emailSentTo}</span>
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                <input
                  type="email"
                  name="email"
                  placeholder="you@yourfirm.com"
                  className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  className="w-full rounded-xl border border-slate-200 pl-11 pr-11 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="w-[18px] h-[18px]" />
                  ) : (
                    <Eye className="w-[18px] h-[18px]" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl py-3 font-bold text-white shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#0F5132" }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Forgot password link */}
          <div className="mt-5 text-center">
            <button
              onClick={handleResetPassword}
              className="text-sm font-medium text-slate-500 hover:text-emerald-700 transition-colors"
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LoginModal;
